import { useParams } from 'react-router-dom'
import { ReactRenderer } from '@tiptap/react'
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import { MentionOptions } from '@tiptap/extension-mention'
import tippy, { Instance, Props as TippyProps } from 'tippy.js'
import { t } from 'src/i18n'
import { MentionList } from '../../components'
import { MentionService } from './useMention.service'
import { LIMIT_CHARS_TO_REQUEST, REQUEST_TIMEOUT } from './useMentionUtils.constants'
import {
  ConvertMentionToHTMLParams,
  ExtractMentionsFromHTMLResponse,
  FailureReason,
  GetEditorExtensionParams,
  ParseMentionsParams,
  UrlPathParams,
  RenderMentionHTMLTagParams,
  ConvertMentionToRawTextParams,
  MentionItemForHTML
} from './useMentionUtils.types'

let timeout: NodeJS.Timeout | null = null

const useMentionUtils = () => {
  const { lang: language } = useParams<UrlPathParams>()

  const mentionHTMLTemplates = {
    a: (mention: MentionItemForHTML) =>
      `<a href="#" data-action="open-profile" data-user-ucode="${mention.ucode}" class="_text-blue _cursor-pointer">@${mention.name}</a>`,

    span: (mention: MentionItemForHTML) =>
      `<span class="mention" data-type="mention" data-id="@user#${mention.ucode}" data-label="${mention.name}" contenteditable="false">@${mention.name}</span>`
  }

  const extractMentionsFromHTML = (html: string) => {
    const elem = document.createElement('div')

    elem.innerHTML = html

    const elements = elem.querySelectorAll('[data-type="mention"]')
    const mentions = Array.from(elements).map((mention) => {
      const id = mention.getAttribute('data-id')
      const label = mention.getAttribute('data-label')

      return {
        id,
        label: `@${label}`
      }
    })

    return mentions as ExtractMentionsFromHTMLResponse
  }

  const parseMentions = ({ text, mentions }: ParseMentionsParams) => {
    const mentionHTMLRegex = /<span[^>]*data-type="mention"[^>]*>(.*?)<\/span>/g
    const normalizedText = text.replace(mentionHTMLRegex, '$1')

    function escapeRegex(string: string) {
      return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    }

    return mentions.reduce((acc, mention) => {
      const label = escapeRegex(mention?.label as string)
      const mentionStringRegex = new RegExp(label, 'g')

      return acc.replace(mentionStringRegex, mention?.id as string)
    }, normalizedText)
  }

  const convertMentionToHTML = ({
    text,
    mentions,
    htmlTag = 'a'
  }: ConvertMentionToHTMLParams): string => {
    const hasValidMentions = Array.isArray(mentions) && mentions.length > 0

    if (!hasValidMentions) {
      return text
    }

    return mentions.reduce((acc, mention) => {
      if (!mention?.tag) return acc

      const regex = new RegExp(mention.tag, 'g')

      if (mention?.failed && mention?.failureReason === FailureReason.NOT_FOUND) {
        return acc.replace(regex, `[${t('comments.mentions.not_found')}]`)
      }

      if (mention?.failed && mention?.failureReason === FailureReason.ERROR) {
        return acc.replace(regex, `[${t('comments.mentions.error')}]`)
      }

      const mentionHTML = mentionHTMLTemplates[htmlTag](mention)

      return acc.replace(regex, mentionHTML)
    }, text)
  }

  const getEditorExtension = ({
    mentionConfig,
    communityId,
    onMentionSuccess,
    onMentionError,
    onMentionClick
  }: GetEditorExtensionParams) => {
    return {
      HTMLAttributes: {
        class: 'mention'
      },
      deleteTriggerWithBackspace: true,
      suggestion: {
        items: /* istanbul ignore next */ async ({ query }: { query: string }) => {
          if (query.trim().length < LIMIT_CHARS_TO_REQUEST) {
            return []
          }

          return new Promise((resolve) => {
            if (timeout) {
              clearTimeout(timeout)
            }

            timeout = setTimeout(
              () =>
                MentionService.fetchMentions({
                  communityId: Number(communityId),
                  searchText: query,
                  size: 10,
                  language,
                  callback: resolve,
                  onSuccess: onMentionSuccess,
                  onError: onMentionError
                }),
              REQUEST_TIMEOUT
            )
          })
        },

        render: /* istanbul ignore next */ () => {
          let reactRenderer: ReactRenderer<any> | null = null
          let popup: Instance[] | null = null

          return {
            onStart: (props: SuggestionProps) => {
              if (!props.clientRect) {
                return
              }

              reactRenderer = new ReactRenderer(MentionList, {
                props: {
                  ...props,
                  onMentionClick
                },
                editor: props.editor
              })

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect,
                appendTo: mentionConfig?.appendTo,
                content: reactRenderer?.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'auto',
                zIndex: mentionConfig?.zIndex || 1041
              } as unknown as TippyProps)
            },

            onUpdate(props: SuggestionProps) {
              reactRenderer?.updateProps(props)

              if (!props.clientRect) {
                return
              }

              popup?.[0]?.setProps({
                getReferenceClientRect: props.clientRect
              } as Partial<TippyProps>)
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                popup?.[0]?.hide()
                return true
              }

              return reactRenderer?.ref?.onKeyDown(props)
            },

            onExit() {
              popup?.[0]?.destroy()
              reactRenderer?.destroy()
            }
          }
        }
      }
    } as unknown as MentionOptions
  }

  const renderMentionHTMLTag = ({ name, ucode }: RenderMentionHTMLTagParams) => {
    return name && ucode
      ? `<span class="mention" data-type="mention" data-id="@user#${ucode}" data-label="${name}" contenteditable="false">@${name}</span> `
      : ''
  }

  const convertMentionToRawText = ({ text, mentions }: ConvertMentionToRawTextParams) => {
    const hasValidMentions = Array.isArray(mentions) && mentions.length > 0

    if (!hasValidMentions) {
      return text
    }

    return mentions.reduce((acc, mention) => {
      const regex = new RegExp(mention?.tag as string, 'g')

      if (mention?.failed && mention?.failureReason === FailureReason.NOT_FOUND) {
        return acc.replace(regex, `[${t('comments.mentions.not_found')}]`)
      }

      if (mention?.failed && mention?.failureReason === FailureReason.ERROR) {
        return acc.replace(regex, `[${t('comments.mentions.error')}]`)
      }

      return acc.replace(regex, `@${mention?.name}` as string)
    }, text)
  }

  const getMentionsCount = (text?: string) => {
    const regex = /@[a-z]+#[\w-]+/g

    return (text?.match(regex) || []).length
  }

  return {
    extractMentionsFromHTML,
    parseMentions,
    convertMentionToHTML,
    getEditorExtension,
    renderMentionHTMLTag,
    convertMentionToRawText,
    getMentionsCount
  }
}

export default useMentionUtils
